import React, { useMemo } from 'react';

interface ProgramRankGaugeProps {
  /** Normalised rank between 0 and 1 (0 → 0 %, 1 → 100 %) */
  rank: number;
  /** Width of the full gauge in pixels (height is half when semi-circle) */
  size?: number;
  /** Stroke thickness in pixels */
  strokeWidth?: number;
}

const ProgramRankGauge: React.FC<ProgramRankGaugeProps> = ({
  rank,
  size = 64,
  strokeWidth = 8,
}) => {
  const percentage = Math.round(rank * 100);

  // Geometry for arc (radius 40 so entire gauge fits within 100 viewBox)
  const radius = 40;
  const circumference = Math.PI * radius; // half circle
  const offset = circumference * (1 - rank);

  const gradientId = useMemo(() => `rankGradient-${Math.random().toString(36).slice(2, 9)}`, []);

  return (
    <div
      className="select-none flex flex-col items-center"
      style={{ width: size }}
      aria-label={`R2H rank ${percentage}%`}
    >
      <svg width={size} height={size / 2} viewBox="0 0 100 50" role="img">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#facc15" /> {/* yellow-400 */}
            <stop offset="100%" stopColor="#f97316" /> {/* orange-500 */}
          </linearGradient>
        </defs>

        {/* Background arc */}
        <path
          d="M10,50 A40,40 0 0 1 90,50"
          strokeWidth={strokeWidth}
          fill="transparent"
          className="stroke-default-300"
        />

        {/* Progress arc */}
        <path
          d="M10,50 A40,40 0 0 1 90,50"
          strokeWidth={strokeWidth}
          fill="transparent"
          stroke={`url(#${gradientId})`}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />

        {/* Percentage text */}
        <text
          x="50"
          y="44"
          textAnchor="middle"
          className="fill-foreground text-[24px] font-semibold"
        >
          {percentage}%
        </text>
      </svg>
      <span className="text-[9px] text-default-500 mt-0">R2H RANK</span>
    </div>
  );
};

export default ProgramRankGauge;
